import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const severityClassMap = {
  high: 'badge-danger',
  medium: 'badge-warning',
  low: 'badge-info',
};

const statusClassMap = {
  active: 'badge-success',
  dismissed: 'badge-warning',
  resolved: 'badge-info',
};

const typeLabels = {
  recurring_spend: 'Recorrente',
  duplicate_recurring: 'Duplicado',
  card_bill_risk: 'Fatura',
  fixed_burn: 'Base fixa',
  liability_pressure: 'Passivos',
};

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString('pt-BR') : 'Nao informado';
}

const PersonalFinanceInsightsPage = () => {
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState('');
  const [insights, setInsights] = useState([]);

  const loadInsights = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/personal-finance/insights', {
        params: {
          refresh: true,
          limit: 12,
        },
      });
      setInsights(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar insights');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInsights();
  }, []);

  const updateStatus = async (insightId, status) => {
    try {
      setUpdatingId(insightId);
      await axios.post(`/api/personal-finance/insights/${insightId}/status`, { status });
      toast.success('Insight atualizado');
      await loadInsights();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao atualizar insight');
    } finally {
      setUpdatingId('');
    }
  };

  const activeInsights = insights.filter((item) => item.status === 'active');
  const dismissedInsights = insights.filter((item) => item.status === 'dismissed');
  const resolvedInsights = insights.filter((item) => item.status === 'resolved');
  const estimatedImpact = activeInsights.reduce((acc, item) => acc + Number(item.estimated_monthly_impact || 0), 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Insights ativos</p>
            <p className="text-2xl font-semibold text-slate-900">{loading ? '...' : String(activeInsights.length)}</p>
            <p className="text-sm text-slate-500">O que merece acao agora</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Impacto mensal estimado</p>
            <p className="text-2xl font-semibold text-slate-900">{loading ? '...' : currency.format(Number(estimatedImpact || 0))}</p>
            <p className="text-sm text-slate-500">Soma dos insights ativos priorizados</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Dismissed</p>
            <p className="text-2xl font-semibold text-slate-900">{loading ? '...' : String(dismissedInsights.length)}</p>
            <p className="text-sm text-slate-500">Insights silenciados pelo usuario</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Resolved</p>
            <p className="text-2xl font-semibold text-slate-900">{loading ? '...' : String(resolvedInsights.length)}</p>
            <p className="text-sm text-slate-500">Sinais que desapareceram no modelo</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="card">
          <div className="card-body space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Insight layer</p>
                <h2 className="text-xl font-semibold text-slate-900">Prioridades acionaveis</h2>
              </div>
              <button className="btn-secondary" onClick={loadInsights} disabled={loading}>
                Atualizar
              </button>
            </div>

            <div className="rounded-3xl border border-emerald-100 bg-emerald-50/70 p-5">
              <p className="text-sm text-emerald-900">
                Esta camada cruza transacoes, forecast, cartoes e patrimonio para reduzir surpresa e cortar desperdicio. Ela nao depende do dominio corporativo.
              </p>
            </div>

            <div className="space-y-4">
              {!loading && activeInsights.length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-500">
                  Nenhum insight ativo agora. Isso pode significar caixa sob controle ou falta de dados suficientes para detectar recorrencias e pressao.
                </div>
              )}

              {activeInsights.map((insight) => (
                <div key={insight.id} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={severityClassMap[insight.severity] || 'badge-info'}>{insight.severity}</span>
                      <span className={statusClassMap[insight.status] || 'badge-info'}>{insight.status}</span>
                      <span className="badge-info">{typeLabels[insight.insight_type] || insight.insight_type}</span>
                    </div>
                    <p className="text-sm text-slate-500">Impacto {currency.format(Number(insight.estimated_monthly_impact || 0))}</p>
                  </div>

                  <div className="mt-4">
                    <p className="text-lg font-semibold text-slate-900">{insight.title}</p>
                    <p className="mt-2 text-sm text-slate-600">{insight.description}</p>
                  </div>

                  <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Acao recomendada</p>
                    <p className="mt-2 text-sm text-emerald-900">{insight.recommended_action || 'Sem acao recomendada.'}</p>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-slate-500">Surfaced em {formatDateTime(insight.surfaced_at)}</p>
                    <div className="flex flex-wrap gap-2">
                      <button className="btn-secondary" onClick={() => updateStatus(insight.id, 'resolved')} disabled={updatingId === insight.id}>
                        {updatingId === insight.id ? 'Salvando...' : 'Marcar resolvido'}
                      </button>
                      <button className="btn-secondary" onClick={() => updateStatus(insight.id, 'dismissed')} disabled={updatingId === insight.id}>
                        {updatingId === insight.id ? 'Salvando...' : 'Dispensar'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card">
            <div className="card-body space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Fila secundaria</p>
                <h2 className="text-xl font-semibold text-slate-900">Dismissed e resolved</h2>
              </div>

              <div className="space-y-3">
                {[...dismissedInsights, ...resolvedInsights].map((insight) => (
                  <div key={insight.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-slate-900">{insight.title}</p>
                        <p className="mt-1 text-sm text-slate-500">{insight.description}</p>
                      </div>
                      <span className={statusClassMap[insight.status] || 'badge-info'}>{insight.status}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                      <p className="text-sm text-slate-500">{typeLabels[insight.insight_type] || insight.insight_type}</p>
                      <button className="btn-secondary" onClick={() => updateStatus(insight.id, 'active')} disabled={updatingId === insight.id}>
                        {updatingId === insight.id ? 'Salvando...' : 'Reativar'}
                      </button>
                    </div>
                  </div>
                ))}
                {!loading && dismissedInsights.length === 0 && resolvedInsights.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                    Nenhum insight fora da fila ativa ainda.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Cobertura do motor</p>
                <h2 className="text-xl font-semibold text-slate-900">O que esta sendo observado</h2>
              </div>
              <div className="space-y-3 text-sm text-slate-600">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  Gastos recorrentes por conta e cartao, com foco em assinaturas e cobrancas repetidas.
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  Pressao de fatura sobre o forecast do caixa nos proximos 30 dias.
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  Base fixa mensal em relacao a renda principal configurada.
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  Peso de passivos e obrigacoes de cartao sobre o patrimonio liquido.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalFinanceInsightsPage;
